// src/providers/AuthProvider.tsx
import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { Session } from "@supabase/supabase-js";
import { AuthService } from "../services/AuthService";
import { useAuthState } from "../hooks/useAuthState";
import {
  startOperation,
  recordSuccess,
  recordError,
  recordEvent,
} from "../utils/authMetrics";
import { CacheService } from "../services/CacheService";
import { ErrorBoundary } from "../components/common/ErrorBoundary";
import { UserSession } from "../types/database.enhanced";
import supabase from "../lib/supabase";

export interface AuthContextType {
  // Core state
  user: UserSession | null;
  session: Session | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isInitialized: boolean;

  // User metadata
  permissions: string[];
  roles: string[];
  features: Record<string, boolean>;

  // Core actions
  signIn: (email: string, password: string) => Promise<void>;
  signInWithProvider: (
    provider: "google" | "github" | "apple",
  ) => Promise<void>;
  signUp: (
    email: string,
    password: string,
    metadata?: Record<string, any>,
  ) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (newPassword: string) => Promise<void>;

  // User management
  updateProfile: (data: Partial<UserSession>) => Promise<void>;
  refreshUser: () => Promise<void>;
  verifyEmail: () => Promise<void>;

  // Session management
  refreshSession: () => Promise<Session | null>;
  getAccessToken: () => Promise<string | null>;

  // Feature flags
  hasPermission: (permission: string) => boolean;
  hasRole: (role: string) => boolean;
  hasFeature: (feature: string) => boolean;

  // Error state
  error: Error | null;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
  onAuthStateChange?: (
    event: "SIGNED_IN" | "SIGNED_OUT" | "USER_UPDATED",
    session: Session | null,
  ) => void;
  onError?: (error: Error) => void;
  config?: {
    enableCache?: boolean;
    cacheTTL?: number;
    enableMetrics?: boolean;
    sessionRefreshInterval?: number;
    maxRetries?: number;
  };
}

export const AuthProvider: React.FC<AuthProviderProps> = ({
  children,
  onAuthStateChange,
  onError,
  config = {
    enableCache: true,
    cacheTTL: 300, // 5 minutes
    enableMetrics: true,
    sessionRefreshInterval: 300000, // 5 minutes
    maxRetries: 3,
  },
}) => {
  const {
    state,
    setUser,
    setSession,
    setLoading,
    setError,
    clearError,
    setInitialized,
  } = useAuthState();
  const processingAuthEventRef = useRef(false);
  const sessionRecoveryAttempted = useRef(false);
  const isSigningOutRef = useRef(false); // Use ref instead of variable

  const authService = useRef(AuthService.getInstance());
  const cacheService = useRef(CacheService.getInstance());
  const refreshInterval = useRef<NodeJS.Timeout | null>(null);
  const retryCount = useRef(0);

  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // Set up auth state change listener
  useEffect(() => {
    const { subscription } = authService.current.onAuthStateChange(
      async (event, session) => {
        // ⭐ CRITICAL FIX: Detect password recovery and prevent auto-login
        if (event === "PASSWORD_RECOVERY") {
          console.log("Password recovery detected - preventing auto-login");

          // Don't update user/session state for recovery
          // Just redirect to reset password page
          if (window.location.pathname !== "/auth/reset-password") {
            window.location.href = "/auth/reset-password";
          }

          return; // Stop here - don't process as SIGNED_IN
        }

        // Prevent processing multiple events simultaneously
        if (processingAuthEventRef.current) {
          return;
        }

        processingAuthEventRef.current = true;

        try {
          recordEvent("auth_state_change", { event });
          const current = stateRef.current; // ← read from ref, not closure
          switch (event) {
            case "SIGNED_IN":
              if (window.location.pathname.includes("reset-password")) return;

              if (session) {
                setSession(session);
                const response = await authService.current.getUserProfile(
                  session.user.id,
                );

                if (response.data) {
                  setUser(response.data);
                  if (config.enableCache) {
                    await cacheService.current.set(
                      "auth_user",
                      response.data,
                      config.cacheTTL,
                    );
                    await cacheService.current.set(
                      "auth_session",
                      session,
                      config.cacheTTL,
                    );
                  }
                } else {
                  // fall back to minimal user — never leave user null while initialized
                  setUser({
                    id: session.user.id,
                    email: session.user.email || "",
                    display_name: null,
                    avatar_url: null,
                    is_verified: false,
                    role: [],
                    created_at: session.user.created_at,
                    full_name: null,
                  });
                }
                setInitialized(true);
              }
              onAuthStateChange?.("SIGNED_IN", session);
              break;

            case "SIGNED_OUT":
              if (current.user || current.session) {
                // ← from ref
                setUser(null);
                setSession(null);
                if (config.enableCache) {
                  await cacheService.current.delete("auth_user");
                  await cacheService.current.delete("auth_session");
                }
                setInitialized(true);
              }
              onAuthStateChange?.("SIGNED_OUT", null);
              break;

            case "USER_UPDATED":
              if (session) {
                setSession(session);
                const response = await authService.current.getUserProfile(
                  session.user.id,
                );
                if (response.data) {
                  setUser(response.data);
                  if (config.enableCache) {
                    await cacheService.current.set(
                      "auth_user",
                      response.data,
                      config.cacheTTL,
                    );
                  }
                }
              }
              onAuthStateChange?.("USER_UPDATED", session);
              break;

            case "TOKEN_REFRESHED":
              if (session) {
                setSession(session);
                if (config.enableCache) {
                  await cacheService.current.set(
                    "auth_session",
                    session,
                    config.cacheTTL,
                  );
                }
              }
              break;
          }
        } finally {
          setTimeout(() => {
            processingAuthEventRef.current = false;
          }, 100);
        }
      },
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [
    config.enableCache,
    config.cacheTTL,
    setUser,
    setSession,
    setInitialized,
    onAuthStateChange,
  ]);

  // Sign in handler
  const signIn = useCallback(
    async (email: string, password: string) => {
      try {
        setLoading(true);
        startOperation("sign_in");

        const response = await authService.current.signIn(email, password);
        if (response.error) throw response.error;
        if (!response.data)
          throw new Error("No session returned after sign in");

        // Populate state BEFORE resolving so callers can safely navigate
        setSession(response.data);

        const profileResponse = await authService.current.getUserProfile(
          response.data.user.id,
        );

        if (profileResponse.data) {
          setUser(profileResponse.data);
          if (config.enableCache) {
            await cacheService.current.set(
              "auth_user",
              profileResponse.data,
              config.cacheTTL,
            );
            await cacheService.current.set(
              "auth_session",
              response.data,
              config.cacheTTL,
            );
          }
        } else {
          // Don't leave user null — fall back to minimal user so ProtectedRoute
          // doesn't bounce us to /login
          setUser({
            id: response.data.user.id,
            email: response.data.user.email || "",
            display_name: null,
            avatar_url: null,
            is_verified: false,
            role: [],
            created_at: response.data.user.created_at,
            full_name: null,
          });
        }

        setInitialized(true);
        recordSuccess("sign_in");
      } catch (error) {
        const authError =
          error instanceof Error ? error : new Error("Sign in failed");
        setError(authError);
        onError?.(authError);
        recordError("sign_in", error);
        throw authError;
      } finally {
        setLoading(false);
      }
    },
    [
      config.enableCache,
      config.cacheTTL,
      setUser,
      setSession,
      setInitialized,
      setLoading,
      setError,
      onError,
    ],
  );

  // Sign in with provider
  const signInWithProvider = useCallback(
    async (provider: "google" | "github" | "apple") => {
      try {
        startOperation("sign_in_with_provider");
        await authService.current.signInWithProvider(provider);
        recordSuccess("sign_in_with_provider");
      } catch (error) {
        const authError =
          error instanceof Error
            ? error
            : new Error(`${provider} sign in failed`);
        setError(authError);
        onError?.(authError);
        recordError("sign_in_with_provider", error);
        throw authError;
      }
    },
    [setError, onError],
  );

  // Sign up handler
  const signUp = useCallback(
    async (email: string, password: string, metadata?: Record<string, any>) => {
      try {
        setLoading(true);
        startOperation("sign_up");

        console.log("=== SIGNUP START ===");
        console.log("Attempting to sign up user:", email);

        const response = await authService.current.signUp(
          email,
          password,
          metadata,
        );

        console.log("Sign up response:", response);

        if (response.error) {
          console.error("Sign up error from AuthService:", response.error);
          throw response.error;
        }

        // Get session after successful signup
        console.log("Getting session after signup...");
        const { data: sessionData, error: sessionError } =
          await authService.current.getSession();

        console.log("Session data user ID:", sessionData?.user?.id);
        console.log("Session error:", sessionError);

        if (sessionData?.user) {
          console.log("=== CREATING PROFILE ===");
          console.log("User ID for profile:", sessionData.user.id);
          console.log("User email:", sessionData.user.email);

          // Wait to ensure auth.users row is created
          await new Promise((resolve) => setTimeout(resolve, 1000));

          try {
            const { data: profileData, error: profileError } = await supabase
              .getClient()
              .from("profiles")
              .upsert(
                {
                  uid: sessionData.user.id,
                  user_id: sessionData.user.id,
                  email: sessionData.user.email || email,
                  full_name: metadata?.full_name || "",
                  profile_setup_completed: false,
                  profile_completion: 0,
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString(),
                },
                {
                  onConflict: "uid",
                },
              )
              .select()
              .single();

            console.log("Profile creation result:", {
              data: profileData,
              error: profileError,
              profileId: profileData?.id,
            });

            if (profileError) {
              console.error("Error creating user profile:", profileError);
            } else {
              console.log("=== PROFILE CREATED SUCCESSFULLY ===");
              console.log("Profile ID:", profileData.id);
              console.log("Profile UID:", profileData.uid);
              console.log("Profile User ID:", profileData.user_id);
            }
          } catch (profileError) {
            console.error("Failed to create profile:", profileError);
          }
        }

        recordSuccess("sign_up");
        console.log("=== SIGNUP COMPLETED ===");
      } catch (error: any) {
        console.error("Sign up failed:", error);
        const authError =
          error instanceof Error
            ? error
            : new Error(error.message || "Sign up failed");
        setError(authError);
        onError?.(authError);
        recordError("sign_up", error);
        throw authError;
      } finally {
        setLoading(false);
      }
    },
    [setLoading, setError, onError],
  );

  // Sign out handler - fixed with useRef
  const handleSignOut = useCallback(async (): Promise<void> => {
    if (isSigningOutRef.current) {
      console.warn("Sign out already in progress");
      return;
    }

    isSigningOutRef.current = true;

    try {
      setLoading(true);
      startOperation("sign_out");

      const response = await authService.current.signOut();
      if (response.error) throw response.error;

      // Clear state
      setUser(null);
      setSession(null);

      // Clear cache
      if (config.enableCache) {
        await cacheService.current.delete("auth_user");
        await cacheService.current.delete("auth_session");
      }

      // Clear intervals
      if (refreshInterval.current) {
        clearInterval(refreshInterval.current);
        refreshInterval.current = null;
      }

      setInitialized(true);
      recordSuccess("sign_out");

      return;
    } catch (error) {
      const authError =
        error instanceof Error ? error : new Error("Sign out failed");
      setError(authError);
      onError?.(authError);
      recordError("sign_out", error);
      throw authError;
    } finally {
      setLoading(false);
      setTimeout(() => {
        isSigningOutRef.current = false;
      }, 1000);
    }
  }, [
    config.enableCache,
    setUser,
    setSession,
    setLoading,
    setError,
    setInitialized,
    onError,
  ]);

  // Refresh session
  const refreshSession = useCallback(async (): Promise<Session | null> => {
    try {
      startOperation("refresh_session");
      const response = await authService.current.refreshSession();

      if (response.error) throw response.error;
      if (!response.data) return null;

      setSession(response.data);

      if (config.enableCache) {
        await cacheService.current.set(
          "auth_session",
          response.data,
          config.cacheTTL,
        );
      }

      recordSuccess("refresh_session");
      return response.data;
    } catch (error) {
      recordError("refresh_session", error);

      // If refresh fails, sign out
      if (
        error instanceof Error &&
        error.message.includes("Refresh token not found")
      ) {
        await handleSignOut();
      }

      return null;
    }
  }, [config.enableCache, config.cacheTTL, setSession, handleSignOut]);

  // Validate and refresh session
  const validateSession = useCallback(
    async (session: Session): Promise<boolean> => {
      try {
        const isValid = await authService.current.validateSession(session);

        if (!isValid) {
          // Session expired, try to refresh
          const refreshed = await refreshSession();
          return !!refreshed;
        }

        return true;
      } catch (error) {
        return false;
      }
    },
    [refreshSession], // Added missing dependency
  );

  // Initialize auth state
  const initializeAuth = useCallback(async () => {
    try {
      setLoading(true);
      startOperation("initialize");

      // Check cache first if enabled
      if (config.enableCache) {
        const cachedUser =
          await cacheService.current.get<UserSession>("auth_user");
        const cachedSession =
          await cacheService.current.get<Session>("auth_session");

        if (cachedUser && cachedSession) {
          setUser(cachedUser);
          setSession(cachedSession);
          setInitialized(true);
          setLoading(false);

          // Validate cached session in background
          validateSession(cachedSession).catch(() => {
            // If validation fails, clear cache and reinitialize
            cacheService.current.delete("auth_user");
            cacheService.current.delete("auth_session");
            initializeAuth();
          });

          recordSuccess("initialize", "cache_hit");
          return;
        }
      }

      // Get fresh session
      const response = await authService.current.getSession();

      if (response.error) {
        throw response.error;
      }

      if (!response.data) {
        setInitialized(true);
        setLoading(false);
        recordSuccess("initialize", "no_session");
        return;
      }

      // Get user profile
      const profileResponse = await authService.current.getUserProfile(
        response.data.user.id,
      );

      if (profileResponse.error) {
        console.warn(
          "Profile fetch failed, using minimal user data:",
          profileResponse.error,
        );
        const minimalUser: UserSession = {
          id: response.data.user.id,
          email: response.data.user.email || "",
          display_name: null,
          avatar_url: null,
          is_verified: false,
          role: [],
          created_at: response.data.user.created_at,
          full_name: null,
        };
        setUser(minimalUser);
      } else if (profileResponse.data) {
        setUser(profileResponse.data);
      }

      setSession(response.data);
      setInitialized(true);

      // Cache results
      if (config.enableCache && profileResponse.data) {
        await cacheService.current.set(
          "auth_user",
          profileResponse.data,
          config.cacheTTL,
        );
        await cacheService.current.set(
          "auth_session",
          response.data,
          config.cacheTTL,
        );
      }

      recordSuccess("initialize", "fresh_load");
    } catch (error) {
      const authError =
        error instanceof Error
          ? error
          : new Error("Auth initialization failed");
      setError(authError);
      onError?.(authError);
      recordError("initialize", authError);
    } finally {
      setLoading(false);
      retryCount.current = 0;
    }
  }, [
    config.enableCache,
    config.cacheTTL,
    setUser,
    setSession,
    setLoading,
    setError,
    setInitialized,
    onError,
    validateSession, // Added missing dependency
  ]);

  // Initialize on mount with retry logic
  useEffect(() => {
    const init = async () => {
      if (retryCount.current < (config.maxRetries || 3)) {
        try {
          await initializeAuth();
        } catch (error) {
          retryCount.current++;
          console.warn(
            `Auth initialization retry ${retryCount.current}/${config.maxRetries}`,
          );

          if (retryCount.current < (config.maxRetries || 3)) {
            setTimeout(init, 1000 * Math.pow(2, retryCount.current)); // Exponential backoff
          } else {
            setError(
              new Error(
                "Failed to initialize authentication after multiple attempts",
              ),
            );
            setInitialized(true);
            setLoading(false);
          }
        }
      }
    };

    init();
  }, [initializeAuth, config.maxRetries, setError, setInitialized, setLoading]);

  // Update profile
  const updateProfile = useCallback(
    async (data: Partial<UserSession>) => {
      try {
        setLoading(true);
        startOperation("update_profile");

        const response = await authService.current.updateProfile(data);
        if (response.error) throw response.error;
        if (!response.data) throw new Error("No user returned after update");

        setUser(response.data);

        if (config.enableCache) {
          await cacheService.current.set(
            "auth_user",
            response.data,
            config.cacheTTL,
          );
        }

        recordSuccess("update_profile");
      } catch (error) {
        const authError =
          error instanceof Error ? error : new Error("Profile update failed");
        setError(authError);
        onError?.(authError);
        recordError("update_profile", error);
        throw authError;
      } finally {
        setLoading(false);
      }
    },
    [
      config.enableCache,
      config.cacheTTL,
      setUser,
      setLoading,
      setError,
      onError,
    ],
  );

  // Refresh user data
  const refreshUser = useCallback(async () => {
    if (!state.user) return;

    try {
      startOperation("refresh_user");
      const response = await authService.current.getUserProfile(state.user.id);

      if (response.error) throw response.error;
      if (!response.data) throw new Error("User not found");

      setUser(response.data);

      if (config.enableCache) {
        await cacheService.current.set(
          "auth_user",
          response.data,
          config.cacheTTL,
        );
      }

      recordSuccess("refresh_user");
    } catch (error) {
      const authError =
        error instanceof Error ? error : new Error("Failed to refresh user");
      setError(authError);
      onError?.(authError);
      recordError("refresh_user", error);
    }
  }, [
    state.user,
    config.enableCache,
    config.cacheTTL,
    setUser,
    setError,
    onError,
  ]);

  // Check permissions
  const hasPermission = useCallback(
    (permission: string): boolean => {
      return state.user?.role?.includes(permission) || false;
    },
    [state.user],
  );

  const hasRole = useCallback(
    (role: string): boolean => {
      return state.user?.role?.includes(role) || false;
    },
    [state.user],
  );

  const hasFeature = useCallback(
    (feature: string): boolean => {
      return state.features[feature] || false;
    },
    [state.features],
  );

  // Get access token
  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (!state.session) return null;

    // Check if token is about to expire (within 5 minutes)
    const expiresAt = new Date(state.session.expires_at || 0);
    const now = new Date();
    const fiveMinutes = 5 * 60 * 1000;

    if (expiresAt.getTime() - now.getTime() < fiveMinutes) {
      const refreshedSession = await refreshSession();
      return refreshedSession?.access_token || null;
    }

    return state.session.access_token;
  }, [state.session, refreshSession]);

  // Wrapper functions that return void instead of AuthResponse
  const wrappedResetPassword = useCallback(
    async (email: string): Promise<void> => {
      const response = await authService.current.resetPassword(email);
      if (response.error) throw response.error;
    },
    [],
  );

  const wrappedUpdatePassword = useCallback(
    async (newPassword: string): Promise<void> => {
      const response = await authService.current.updatePassword(newPassword);
      if (response.error) throw response.error;
    },
    [],
  );

  const wrappedVerifyEmail = useCallback(async (): Promise<void> => {
    const response = await authService.current.verifyEmail();
    if (response.error) throw response.error;
  }, []);

  const contextValue: AuthContextType = {
    // Core state
    user: state.user,
    session: state.session,
    isLoading: state.isLoading,
    isAuthenticated: !!state.user && !!state.session,
    isInitialized: state.isInitialized,

    // User metadata
    permissions: state.user?.role || [],
    roles: state.user?.role || [],
    features: state.features,

    // Core actions
    signIn,
    signInWithProvider,
    signUp,
    signOut: handleSignOut,
    resetPassword: wrappedResetPassword,
    updatePassword: wrappedUpdatePassword,

    // User management
    updateProfile,
    refreshUser,
    verifyEmail: wrappedVerifyEmail,

    // Session management
    refreshSession,
    getAccessToken,

    // Feature checks
    hasPermission,
    hasRole,
    hasFeature,

    // Error state
    error: state.error,
    clearError,
  };

  // Set up periodic session refresh
  useEffect(() => {
    if (state.session && config.sessionRefreshInterval) {
      refreshInterval.current = setInterval(async () => {
        await refreshSession();
      }, config.sessionRefreshInterval);
    }

    return () => {
      if (refreshInterval.current) {
        clearInterval(refreshInterval.current);
      }
    };
  }, [state.session, config.sessionRefreshInterval, refreshSession]);

  useEffect(() => {
    const recoverFromStorage = async () => {
      // Fix: Use state.user and state.session instead of state.isAuthenticated
      if (
        sessionRecoveryAttempted.current ||
        (state.user && state.session) || // This is equivalent to isAuthenticated
        state.isLoading
      ) {
        return;
      }

      sessionRecoveryAttempted.current = true;

      try {
        console.log("Attempting to recover session from storage...");

        // Check localStorage directly
        const storageKey = "orra-auth-token";
        const storedSession = localStorage.getItem(storageKey);

        if (storedSession) {
          console.log("Found stored session token");

          // Try to refresh the session
          const refreshed = await refreshSession();

          if (refreshed) {
            console.log("Session successfully recovered");

            // Get fresh user data
            const {
              data: { user },
            } = await supabase.getClient().auth.getUser();
            if (user) {
              const profileResponse = await authService.current.getUserProfile(
                user.id,
              );
              if (profileResponse.data) {
                setUser(profileResponse.data);
              }
            }
          }
        }
      } catch (error) {
        console.error("Session recovery failed:", error);
        // Clear potentially corrupted storage
        localStorage.removeItem("orra-auth-token");
      }
    };

    if (!state.isInitialized) {
      recoverFromStorage();
    }
  }, [
    state.isInitialized,
    state.user,
    state.session,
    state.isLoading,
    refreshSession,
    setUser,
  ]);

  return (
    <ErrorBoundary
      fallback={<div>Authentication error. Please refresh the page.</div>}
      onError={(error) => {
        setError(error);
        onError?.(error);
        recordError("error_boundary", error);
      }}
    >
      <AuthContext.Provider value={contextValue}>
        {children}
      </AuthContext.Provider>
    </ErrorBoundary>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const useAuthStatus = () => {
  const auth = useAuth();
  return {
    isAuthenticated: auth.isAuthenticated,
    isLoading: auth.isLoading,
    isInitialized: auth.isInitialized,
    user: auth.user,
    error: auth.error,
  };
};
